import { useState, type ChangeEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import type { LucideIcon } from "lucide-react";
import { AlertTriangle, CheckCircle2, ChevronRight, Inbox, Loader2 } from "lucide-react";
import type { NoticeTone } from "../types";

export const inputClass =
  "w-full rounded-md border border-border bg-surface px-3.5 py-2.5 text-sm text-content-primary shadow-[0_1px_2px_rgba(18,40,47,0.03)] transition-colors placeholder:text-content-disabled hover:border-border-strong focus:border-interactive focus:outline-none focus:ring-2 focus:ring-interactive/15 disabled:bg-surface-muted disabled:text-content-disabled";
export const labelClass = "grid content-start gap-1.5 text-sm font-medium text-content-primary";
export const textareaClass = `${inputClass} min-h-24 resize-y`;
export const secondaryButtonClass =
  "inline-flex items-center justify-center gap-2 rounded-md border border-border bg-surface px-4 py-2.5 text-sm font-semibold text-content-primary shadow-sm transition-all hover:bg-surface-muted disabled:cursor-not-allowed disabled:text-content-disabled";
export const primaryButtonClass =
  "inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-content-on-primary shadow-sm transition-all hover:bg-primary-hover hover:shadow-md disabled:cursor-not-allowed disabled:bg-surface-muted disabled:text-content-disabled disabled:shadow-none";
export const dangerButtonClass =
  "inline-flex items-center justify-center gap-2 rounded-md bg-danger px-4 py-2.5 text-sm font-semibold text-content-on-primary shadow-sm transition-all hover:brightness-95 hover:shadow-md disabled:cursor-not-allowed disabled:bg-surface-muted disabled:text-content-disabled disabled:shadow-none";

export function errorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : "Unexpected error";
}

export function LanguageSwitcher({ dark = false, compact = false }) {
  const { i18n, t } = useTranslation();
  return (
    <label className={`flex items-center gap-2 text-sm ${compact ? "md:hidden" : ""}`}>
      {!compact && (
        <span className={dark ? "text-white/70" : "text-content-secondary"}>{t("language")}</span>
      )}
      <select
        className={
          dark || compact
            ? "rounded-md border border-white/20 bg-white/10 px-2 py-1.5 text-sm text-white focus:outline-none"
            : "rounded-md border border-border bg-surface px-2 py-1.5 text-sm text-content-primary focus:border-interactive focus:outline-none"
        }
        value={i18n.language}
        onChange={(event: ChangeEvent<HTMLSelectElement>) =>
          void i18n.changeLanguage(event.target.value)
        }
      >
        <option value="en">{t("english")}</option>
        <option value="ur">{t("urdu")}</option>
      </select>
    </label>
  );
}

interface CardProps {
  title: string;
  text?: string;
  children: ReactNode;
}

export function Card({ title, text, children }: CardProps) {
  return (
    <section className="mb-6 rounded-lg border border-border bg-surface p-5 shadow-sm md:p-6">
      <div>
        <h2 className="text-lg font-semibold tracking-tight text-content-primary">{title}</h2>
        {text && <p className="mt-1 text-sm leading-6 text-content-secondary">{text}</p>}
      </div>
      <div className="mt-5">{children}</div>
    </section>
  );
}

interface LinkTileProps {
  to: string;
  icon: LucideIcon;
  title: string;
  text: string;
  tone?: string;
}

export function ActionTile({ to, icon: Icon, title, text, tone = "slate" }: LinkTileProps) {
  return (
    <NavLink
      to={to}
      className="group flex min-h-32 items-start gap-3 rounded-md border border-border bg-surface p-4 transition-all hover:-translate-y-0.5 hover:border-border-strong hover:shadow-sm"
    >
      <span className={`metric-icon metric-icon-${tone}`}>
        <Icon size={20} strokeWidth={1.8} />
      </span>
      <span className="min-w-0 flex-1 pt-1">
        <strong className="block text-sm font-semibold text-content-primary">{title}</strong>
        <span className="mt-1.5 block text-sm leading-5 text-content-secondary">{text}</span>
      </span>
      <ChevronRight
        size={16}
        className="mt-2 text-content-disabled transition-transform group-hover:translate-x-0.5"
      />
    </NavLink>
  );
}

export function SetupRow({ to, icon: Icon, title, text }: LinkTileProps) {
  return (
    <NavLink
      to={to}
      className="group flex items-center gap-3 bg-surface px-4 py-3.5 transition-colors hover:bg-surface-muted"
    >
      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-interactive/10 text-interactive">
        <Icon size={18} strokeWidth={1.8} />
      </span>
      <span className="min-w-0 flex-1">
        <strong className="block text-sm font-semibold text-content-primary">{title}</strong>
        <span className="mt-0.5 block truncate text-sm text-content-secondary">{text}</span>
      </span>
      <span className="hidden rounded-full bg-surface-muted px-3 py-1 text-xs font-medium text-content-secondary sm:block">
        Manage
      </span>
      <ChevronRight
        size={17}
        className="text-content-disabled transition-transform group-hover:translate-x-0.5"
      />
    </NavLink>
  );
}

export function Field({
  name,
  type = "text",
  required = true,
}: {
  name: string;
  type?: string;
  required?: boolean;
}) {
  return (
    <label className={labelClass}>
      {name.replaceAll("_", " ")}
      <input className={inputClass} name={name} type={type} required={required} />
    </label>
  );
}

interface TableHeader {
  label: string;
  hide?: "sm" | "md" | "lg";
}

// Selection is opt-in: pass rowIds alongside a selectedIds set and the two
// toggle callbacks to get a checkbox column; omit them and Table renders
// exactly as it always has, so every existing caller is unaffected.
export interface TableSelection {
  // A row without a stable id (e.g. still being created) is simply
  // unselectable — its checkbox cell is omitted rather than rendered disabled.
  rowIds: (string | undefined)[];
  selectedIds: Set<string>;
  onToggleRow: (id: string) => void;
  onToggleAll: () => void;
}

export function Table({
  headers,
  rows,
  selection,
}: {
  headers: TableHeader[];
  rows: ReactNode[][];
  selection?: TableSelection;
}) {
  const hideClass = {
    sm: "hidden sm:table-cell",
    md: "hidden md:table-cell",
    lg: "hidden lg:table-cell",
  };
  const selectableIds = selection?.rowIds.filter((id): id is string => id !== undefined) ?? [];
  const allSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selection!.selectedIds.has(id));
  return (
    <div className="-mx-5 overflow-x-auto px-5 md:-mx-6 md:px-6">
      <table className="w-full min-w-[560px] border-separate border-spacing-0 text-start text-sm">
        <thead>
          <tr>
            {selection && (
              <th className="w-10 border-y border-border bg-surface-muted px-3 py-2.5 text-start first:rounded-s-md first:border-s">
                <input
                  type="checkbox"
                  aria-label="Select all rows"
                  checked={allSelected}
                  onChange={selection.onToggleAll}
                />
              </th>
            )}
            {headers.map((header) => (
              <th
                key={header.label}
                className={`border-y border-border bg-surface-muted px-3 py-2.5 text-start text-xs font-semibold uppercase tracking-wide text-content-secondary first:rounded-s-md first:border-s last:rounded-e-md last:border-e ${header.hide ? hideClass[header.hide] : ""}`}
              >
                {header.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => {
            const rowID = selection?.rowIds[rowIndex];
            return (
              <tr key={rowIndex} className="group transition-colors hover:bg-surface-muted/70">
                {selection && rowID !== undefined && (
                  <td className="border-b border-border px-3 py-3.5">
                    <input
                      type="checkbox"
                      aria-label="Select row"
                      checked={selection.selectedIds.has(rowID)}
                      onChange={() => selection.onToggleRow(rowID)}
                    />
                  </td>
                )}
                {row.map((cell, cellIndex) => (
                  <td
                    key={cellIndex}
                    className={`border-b border-border px-3 py-3.5 text-content-primary ${headers[cellIndex]?.hide ? hideClass[headers[cellIndex].hide!] : ""}`}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// A sticky bar that appears once at least one row is selected, offering a
// bulk delete action. Kept generic (a plain count + label) so every entity
// table — teachers, schools, modules, etc — can reuse it as-is.
export function BulkActionsBar({
  count,
  onDelete,
  onClear,
  busy = false,
  deleteLabel = "Delete selected",
  extra,
}: {
  count: number;
  onDelete: () => void;
  onClear: () => void;
  busy?: boolean;
  deleteLabel?: string;
  /** Another action beside the destructive one, such as Restore. */
  extra?: ReactNode;
}) {
  if (count === 0) return null;
  return (
    <div className="sticky top-0 z-10 -mx-5 mb-4 flex items-center justify-between gap-3 border-b border-border bg-surface px-5 py-3 shadow-sm md:-mx-6 md:px-6">
      <span className="text-sm font-medium text-content-primary">{count} selected</span>
      <div className="flex items-center gap-3">
        <button
          type="button"
          className="text-sm font-medium text-content-secondary hover:text-content-primary"
          onClick={onClear}
          disabled={busy}
        >
          Clear
        </button>
        {extra}
        <button type="button" className={dangerButtonClass} onClick={onDelete} disabled={busy}>
          {busy && <Loader2 size={16} className="animate-spin" />}
          {deleteLabel}
        </button>
      </div>
    </div>
  );
}

export function Badge({
  tone = "neutral",
  children,
}: {
  tone?: "success" | "neutral" | "danger";
  children: ReactNode;
}) {
  const tones = {
    success: "bg-success-bg text-success-text",
    neutral: "bg-surface-muted text-content-secondary",
    danger: "bg-danger-bg text-danger",
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold capitalize ${tones[tone]}`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
      {children}
    </span>
  );
}

export function InlineNotice({
  tone,
  text,
  breakAll = false,
}: {
  tone: NoticeTone;
  text: string;
  breakAll?: boolean;
}) {
  const styles = {
    success: "bg-success-bg text-success-text",
    error: "bg-danger-bg text-danger",
    info: "bg-interactive/10 text-interactive",
  };
  const Icon = tone === "error" ? AlertTriangle : tone === "success" ? CheckCircle2 : Loader2;
  return (
    <p
      className={`mt-4 flex items-start gap-2 rounded-md border border-current/10 px-3.5 py-3 text-sm leading-5 ${styles[tone]} ${breakAll ? "break-all" : ""}`}
    >
      <Icon size={16} className={`shrink-0 ${tone === "info" ? "animate-spin" : ""}`} />
      {text}
    </p>
  );
}

export function LoadingState({ message }: { message: string }) {
  return (
    <div className="grid gap-3 py-4" aria-label={message}>
      {["w-full", "w-5/6", "w-2/3"].map((width) => (
        <div key={width} className={`h-11 ${width} rounded-md bg-surface-muted`} />
      ))}
      <span className="sr-only">{message}</span>
    </div>
  );
}

export function EmptyState({ title, message }: { title: string; message: string }) {
  return (
    <div className="grid justify-items-center gap-2 rounded-md border border-dashed border-border-strong bg-surface-muted/40 px-5 py-12 text-center">
      <div className="grid h-11 w-11 place-items-center rounded-full bg-surface text-content-secondary shadow-sm">
        <Inbox size={20} />
      </div>
      <h3 className="text-base font-medium text-content-primary">{title}</h3>
      <p className="text-sm text-content-secondary">{message}</p>
    </div>
  );
}

export function ConfirmModal({
  open,
  title,
  message,
  confirmLabel = "Delete",
  busy = false,
  requireText,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  busy?: boolean;
  /** For what cannot be undone: the button stays off until this word is typed. */
  requireText?: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  if (!open) return null;
  return (
    <ConfirmDialog
      title={title}
      message={message}
      confirmLabel={confirmLabel}
      busy={busy}
      requireText={requireText}
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  );
}

// Mounted only while the dialog is open, so what was typed never outlives it.
function ConfirmDialog({
  title,
  message,
  confirmLabel,
  busy,
  requireText,
  onConfirm,
  onCancel,
}: {
  title: string;
  message: string;
  confirmLabel: string;
  busy: boolean;
  requireText?: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const [typed, setTyped] = useState("");
  const armed = !requireText || typed.trim().toLowerCase() === requireText.toLowerCase();
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/30 p-4 backdrop-blur-sm md:left-64">
      <div className="w-full max-w-sm rounded-lg border border-border bg-surface p-5 shadow-lg">
        <h3 className="text-base font-semibold text-content-primary">{title}</h3>
        <p className="mt-2 text-sm leading-5 text-content-secondary">{message}</p>
        {requireText && (
          <label className="mt-4 grid gap-1.5 text-sm font-medium text-content-primary">
            Type {requireText} to confirm
            <input
              className={inputClass}
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              autoComplete="off"
            />
          </label>
        )}
        <div className="mt-5 flex items-center justify-center gap-3">
          <button
            type="button"
            className={dangerButtonClass}
            onClick={onConfirm}
            disabled={busy || !armed}
          >
            {busy && <Loader2 size={16} className="animate-spin" />}
            {confirmLabel}
          </button>
          <button
            type="button"
            className="text-sm font-medium text-content-secondary hover:text-content-primary"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export function ErrorState({ message }: { message: string }) {
  return (
    <div className="grid justify-items-center gap-2 rounded-md border border-danger/10 bg-danger-bg/40 px-5 py-12 text-center">
      <div className="grid h-12 w-12 place-items-center rounded-full bg-danger-bg text-danger">
        <AlertTriangle size={20} />
      </div>
      <h3 className="text-base font-medium text-content-primary">Could not load data</h3>
      <p className="text-sm text-content-secondary">{message}</p>
    </div>
  );
}
